/**
 * CART STATE MANAGER UTILITIES
 *
 * Centralized state management system for all cart-related operations in the Shopify theme.
 * Provides a single source of truth for cart data, manages real-time updates, and coordinates
 * communication between all cart components through a comprehensive event system.
 *
 * **DATA SOURCE**: All cart state is sourced exclusively from the `cart-json` Liquid section,
 * which provides enriched cart data with UI-specific properties. The standard Shopify Cart AJAX
 * API (`/cart.json`) is NOT used as the source of that enriched payload — ensuring consistency and
 * eliminating data synchronization issues for line-item display data.
 *
 * Used by: cart-drawer.js, cart-item.js, cart-items.js, cart-note.js, cart-discounts.js,
 *          free-shipping-banner.js, buy-more-save-more-banner.js, and all CartComponentBase extensions
 *
 * Key Features:
 * - Real-time cart state synchronization via cart-json section rendering
 * - Event-driven architecture for component communication
 * - Automatic cart count updates across all UI elements
 * - Enriched cart data with UI-specific properties (pricing, flags, messaging)
 * - Visibility change handling for state refresh
 * - Comprehensive error handling and retry mechanisms
 * - Memory-efficient caching and cleanup patterns
 * - Support for bulk operations and complex cart manipulations
 * - Bundled section rendering for optimal performance (fewer network requests)
 * - Configures Shopify's standard storefront actions (see below)
 *
 * Integration Patterns:
 * - CartComponentBase classes: Access via `this.cartStateManager` getter
 * - External components: Import `addToCart` from `@utilities/theme`
 * - Global access: Available as `window.cartStateManager` and `window.theme.utilities.cart`
 * - Legacy support: `window.addToCart()` and `window.openCartDrawer()` functions
 *
 * Event System (via pub-sub — see utilities-events.js):
 * - greyson:cart:changed  (sticky)  Published when cart state changes
 * - greyson:cart:refreshed          Published after visibility-triggered refreshes
 * - greyson:cart:initial-load (sticky)  Published once on first state load
 * - greyson:cart:item:adding/added/removed  Published around item operations
 * - greyson:cart:system:ready   (sticky)  Published when both state manager and drawer are ready
 *
 * For every mutation that goes through #handleStandardUpdateCart (i.e. every
 * mutation reachable via Shopify.actions.updateCart), greyson:cart:changed/
 * item:added/item:removed are published as a CONSEQUENCE of the matching
 * shopify:cart:* standard event having fired, not proactively ahead of it — see
 * #handleStandardUpdateCart's doc comment for the mechanism. greyson:cart:
 * item:adding and greyson:cart:refreshed have no shopify:* equivalent, so they
 * remain self-published immediately.
 *
 * Operation failures during a standard-action-driven mutation rethrow, so
 * Shopify's runtime emits shopify:cart:error, which cart-drawer.js listens for —
 * no separate greyson:cart:error fires for that path, since nothing needs a
 * second, theme-only failure event where a stable standard one already exists.
 * cart-note.js still publishes greyson:cart:error directly on its own catch
 * (rather than relying solely on shopify:cart:error), since it predates
 * attributes routing through the standard action and existing subscribers
 * depend on it.
 *
 * Shopify Standard Storefront Actions:
 * https://shopify.dev/docs/storefronts/themes/best-practices/standard-actions
 * This class configures Shopify.actions.updateCart/openCart so that external
 * apps/agents calling those actions trigger this theme's real Ajax Cart +
 * cart-json enrichment flow (see #configureStandardActions/#handleStandardUpdateCart)
 * instead of Shopify's default Storefront API + page-reload fallback.
 *
 * Auto-initialization:
 * - Initializes automatically on DOMContentLoaded
 * - Sets up global references and theme integration
 * - Configures visibility change listeners for state synchronization
 *
 * @example
 * // Access from CartComponentBase subclass
 * const stateManager = this.cartStateManager;
 * await stateManager.addItems([{id: 123, quantity: 1}]);
 *
 * @example
 * // Access from external component
 * import { addToCart } from '@utilities/theme';
 * await addToCart({id: 123, quantity: 1});
 *
 * @example
 * // Listen for cart changes via pub-sub
 * import { subscribe } from '@utilities/events';
 * subscribe('greyson:cart:changed', (data) => {
 *   console.log('Cart updated:', data.state);
 * });
 *
 * @namespace CartStateManagerUtilities
 * @since 2024
 * @version 4.0.0
 */

import { publish, EVENTS } from '@utilities/events';

/**
 * @param {Record<string, unknown>} item
 * @returns {Record<string, unknown>}
 */
function sanitizeCartAddItem(item) {
  const sanitized = {
    ...item,
    properties:
      item.properties && typeof item.properties === 'object'
        ? { ...item.properties }
        : {},
  };
  return sanitized;
}

/**
 * Cart State Manager Class
 * @class CartStateManager
 */
export class CartStateManager {
  /** @type {number} Frames spent waiting for Shopify's standard-actions bundle */
  #standardActionsRetries = 0;

  /**
   * Frame budget for that wait. Shopify injects the bundle later in the document
   * than this module, so a handful of frames covers the gap on a normal load.
   * @type {number}
   */
  #standardActionsRetryLimit = 120;

  constructor() {
    /** @type {Object} Current cart state - populated entirely from cart-json section */
    this.state = {
      note: "",
      attributes: {},
      currency: "USD",
      renderCartNote: true,
      itemCount: 0,
      totalPrice: 0,
      totalPriceMoney: "$0",
      inclusionsOnlySubtotal: 0,
      totalDiscount: 0,
      discountApplications: [],
      items: [],
      compareAtPriceTotals: {},
      finalDiscountTotals: {},
    };

    /** @type {boolean} Whether the state is currently being updated */
    this.isUpdating = false;

    /** @type {Map} Cache for product data */
    this.productCache = new Map();

    /** @type {boolean} Whether debug mode is enabled */
    this.debug = false;

    /** @type {boolean} Whether the initial state has been loaded */
    this.initialStateLoaded = false;

    /** @type {cart-drawer|null} Reference to active cart drawer */
    this.drawer = null;

    /** @type {Object|null} Pending changes when events are suppressed */
    this._pendingChanges = null;

    /** @type {Object|null} Previous state when events are suppressed */
    this._previousState = null;

    /** @type {number|null} Debounce timer for network-driven refreshes */
    this._networkRefreshTimer = null;

    /** @type {boolean} Prevent overlapping network-driven refreshes */
    this._networkRefreshInFlight = false;

    /**
     * Count of cart mutation requests this class has in flight.
     *
     * The network observer watches the same /cart/*.js endpoints this class
     * posts to, so without this counter every internal mutation also triggers
     * an observer refresh — a second ?sections=cart-json fetch and a redundant
     * greyson:cart:refreshed publish on top of the greyson:cart:changed the
     * calling method already published (or scheduled). `isUpdating` cannot
     * serve this purpose: it is
     * cleared in each method's finally block, which runs before the observer's
     * 180ms debounce fires.
     * @type {number}
     */
    this._internalMutationDepth = 0;
  }

  /**
   * Checks if debug mode is enabled for this component
   * @returns {boolean} Whether debug logging is enabled
   */
  allowDebug() {
    return this.debug;
  }

  /**
   * Enable or disable debug mode
   * @param {boolean} enabled - Whether to enable debug mode
   */
  setDebugMode(enabled) {
    this.debug = !!enabled;
    console.log(
      `CartStateManager: Debug mode ${enabled ? "enabled" : "disabled"}`
    );
  }

  /**
   * Whether addItems() should post one batched `/cart/add.js` request
   * ({ items: [...] }) instead of the default sequential single-item requests.
   *
   * Sequential is the correct production behavior (see addItems' comment on
   * Web Pixel visibility), so this defaults to off. It exists purely as a
   * manual testing toggle — e.g. to reproduce how a bundle add behaves against
   * Shopify's batched-add code path, or to compare pixel/event output between
   * the two request shapes. Toggle from devtools:
   *   window.cartStateManager.setBulkAddMode(true)
   * @returns {boolean}
   */
  allowBulkAdd() {
    return !!window.__cartAddBulkMode;
  }

  /**
   * Toggle bulk (batched) add-to-cart requests for manual testing.
   * @param {boolean} enabled
   */
  setBulkAddMode(enabled) {
    window.__cartAddBulkMode = !!enabled;
    console.log(
      `CartStateManager: Bulk add-to-cart mode ${enabled ? "enabled" : "disabled"} (testing only — production stays sequential)`
    );
  }





  /**
   * Initialize the cart state manager
   */
  async initialize() {
    // Seed debug mode from the cart-drawer's "debug" class, matching the
    // classList.contains('debug') convention used by every other cart
    // component in this theme (see CartComponentBase#allowDebug).
    this.debug = document.querySelector("cart-drawer")?.classList.contains("debug") ?? false;

    if (this.allowDebug()) {
      console.log("CartStateManager: Initializing cart state manager");
    }

    // Load initial cart state from Shopify
    await this.refreshState();

    // Set up visibility change listener
    this._setupVisibilityChangeListener();
    this._setupCartNetworkObserver();
    this.#configureStandardActions();

    // Set up global references
    window.cartStateManager = this;

    // Set theme.utilities.cart reference for consistency with other utilities
    if (window.theme?.utilities) {
      window.theme.utilities.cart = this;

      // Debug logging for utilities readiness
      if (this.allowDebug()) {
        console.log("CartStateManager: Theme utilities available", {
          availableUtilities: Object.keys(window.theme.utilities),
          cartUtilitySet: !!window.theme.utilities.cart,
          drawerManagerAvailable: !!window.theme.utilities.cartDrawerManager
        });
      }

      // Set the drawer property to the single cart drawer on the page
      this.drawer = document.querySelector("cart-drawer");
      if (this.allowDebug()) {
        console.log("CartStateManager: Set drawer property", {
          hasDrawer: !!this.drawer,
          drawerId: this.drawer?.id || "none",
        });
      }

      if (this.allowDebug()) {
        console.log("CartStateManager: Added to window.theme.utilities.cart");
      }
    } else {
      console.warn(
        "CartStateManager: window.theme.utilities not available yet"
      );
    }


    // Check if cart system is ready now that state manager is initialized
    this._checkAndDispatchCartSystemReady();

    if (this.allowDebug()) {
      console.log("CartStateManager: Initialization complete", {
        itemCount: this.state.itemCount,
        totalPrice: this.state.totalPriceMoney,
        itemsSubtotal: this.state.inclusionsOnlySubtotal,
      });
    }
  }

  /**
   * Publish a cart event via the global pub-sub bus.
   * @param {string} eventName - Event name to publish
   * @param {Object} data - Payload delivered to subscribers
   * @param {Object} [options] - Publish options (e.g. { sticky: true })
   * @private
   */
  _publish(eventName, data, options) {
    if (this.allowDebug()) {
      console.log("CartStateManager: Publishing event", {
        type: eventName,
        data,
        sticky: !!options?.sticky,
      });
    }

    publish(eventName, data, options);
  }

  /**
   * Set up document visibility change listener
   * @private
   */
  _setupVisibilityChangeListener() {
    document.addEventListener('visibilitychange', async () => {
      if (document.visibilityState === 'visible') {
        if (this.allowDebug()) {
          console.log('CartStateManager: Document became visible, refreshing cart state');
        }

        try {
          await this.refreshState({
            source: 'visibility-change',
            force: true // Always force refresh on visibility change to ensure UI updates
          });

          this._publish(EVENTS.CART_STATE_REFRESHED, {
            state: this.state,
            reason: 'visibility-change',
            timestamp: Date.now()
          });

          if (this.allowDebug()) {
            console.log('CartStateManager: State refreshed due to visibility change', {
              itemCount: this.state.itemCount,
              totalPrice: this.state.totalPriceMoney
            });
          }
        } catch (error) {
          console.error('CartStateManager: Error refreshing state on visibility change:', error);
        }
      }
    });

    if (this.allowDebug()) {
      console.log('CartStateManager: Visibility change listener set up');
    }
  }

  /**
   * Determine whether a URL targets Shopify cart *.js endpoints.
   * Matches locale-prefixed and non-prefixed cart mutation endpoints only.
   * Intentionally excludes plain /cart.js reads to avoid reacting to app polling.
   * @param {string} url
   * @returns {boolean}
   * @private
   */
  _isCartJsEndpoint(url) {
    if (!url) return false;
    try {
      const parsed = new URL(url, window.location.origin);
      const pathname = parsed.pathname || "";
      return /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?cart\/(?:add|update|change|clear)\.js$/i.test(pathname);
    } catch {
      return false;
    }
  }

  /**
   * POST to a cart mutation endpoint, marked as originating from this class so
   * the network observer does not schedule a duplicate refresh for it.
   *
   * The marker is held for a short window after the response resolves, because
   * the observer debounces for 180ms after seeing the request. Releasing the
   * marker immediately would let that timer fire against an unmarked state.
   * @param {string} url - Cart endpoint URL
   * @param {Object} body - Request body, serialized as JSON
   * @returns {Promise<Response>}
   * @private
   */
  async _postCartMutation(url, body) {
    this._internalMutationDepth += 1;

    try {
      return await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });
    } finally {
      window.setTimeout(() => {
        this._internalMutationDepth = Math.max(0, this._internalMutationDepth - 1);
      }, 300);
    }
  }

  /**
   * Schedule a debounced refresh after cart network calls complete.
   * @param {string} source
   * @private
   */
  _scheduleNetworkRefresh(source = "network-observer") {
    // Mutations this class issued already refresh state and publish their own
    // events; observing them would only duplicate that work.
    if (this._internalMutationDepth > 0) {
      if (this.allowDebug()) {
        console.log("CartStateManager: skipping observer refresh for internal mutation", { source });
      }
      return;
    }

    if (this._networkRefreshTimer) {
      window.clearTimeout(this._networkRefreshTimer);
    }

    this._networkRefreshTimer = window.setTimeout(() => {
      this._networkRefreshTimer = null;
      this._runNetworkRefresh(source);
    }, 180);
  }

  /**
   * Run a guarded cart refresh after network observation.
   * @param {string} source
   * @private
   */
  async _runNetworkRefresh(source = "network-observer") {
    if (this._networkRefreshInFlight) return;
    if (this.isUpdating) return;

    this._networkRefreshInFlight = true;
    try {
      await this.refreshState({
        source: `network-observer:${source}`,
        force: true,
      });
      this._publish(EVENTS.CART_STATE_REFRESHED, {
        state: this.state,
        reason: "network-observer",
        source,
        timestamp: Date.now(),
      });

      // This refresh only runs for writes this class did not itself issue
      // (_scheduleNetworkRefresh skips internal mutations) — i.e. some other
      // script posted to a cart endpoint directly, bypassing
      // Shopify.actions.updateCart, so no shopify:cart:* event fired for it.
      // See CART_STATE_EXTERNAL_WRITE's doc for why this is a theme-only event
      // rather than a guessed shopify:cart:lines-update.
      this._publish(EVENTS.CART_STATE_EXTERNAL_WRITE, {
        cart: this.#buildStorefrontCartShape(),
        source,
        timestamp: Date.now(),
      });
    } catch (error) {
      if (this.allowDebug()) {
        console.warn("CartStateManager: network-observer refresh failed", { source, error });
      }
    } finally {
      this._networkRefreshInFlight = false;
    }
  }

  /**
   * Patch fetch/XHR once to observe cart *.js calls and trigger refresh.
   * @private
   */
  _setupCartNetworkObserver() {
    if (window.__cartNetworkObserverInstalled) return;
    window.__cartNetworkObserverInstalled = true;

    const originalFetch = window.fetch.bind(window);
    window.fetch = async (...args) => {
      const response = await originalFetch(...args);
      try {
        const input = args?.[0];
        const requestUrl = typeof input === "string" ? input : input?.url;
        if (response?.ok && this._isCartJsEndpoint(requestUrl)) {
          this._scheduleNetworkRefresh(`fetch:${requestUrl}`);
        }
      } catch {
        // Best-effort observer only
      }
      return response;
    };

    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function patchedCartObserverOpen(method, url, ...rest) {
      this.__cartObserverUrl = url;
      return originalOpen.call(this, method, url, ...rest);
    };

    XMLHttpRequest.prototype.send = function patchedCartObserverSend(...args) {
      this.addEventListener("loadend", () => {
        try {
          const requestUrl = this.__cartObserverUrl;
          if (this.status >= 200 && this.status < 300 && window.cartStateManager?._isCartJsEndpoint?.(requestUrl)) {
            window.cartStateManager._scheduleNetworkRefresh(`xhr:${requestUrl}`);
          }
        } catch {
          // Best-effort observer only
        }
      }, { once: true });
      return originalSend.call(this, ...args);
    };

    if (this.allowDebug()) {
      console.log("CartStateManager: cart network observer installed");
    }
  }

  /**
   * Configure Shopify's Standard Storefront Actions (Shopify.actions) so that
   * external apps/agents calling updateCart/openCart trigger this theme's real
   * cart behavior instead of the default Storefront API + page reload fallback.
   *
   * See: https://shopify.dev/docs/storefronts/themes/best-practices/standard-actions
   *
   * `updateCart` is configured with a custom `handler` that ignores the
   * Storefront-API-based `defaultHandler` entirely — this theme's cart state is
   * sourced exclusively from the Ajax Cart + enriched `cart-json` section (see
   * file header), not the Storefront GraphQL API, so the handler translates the
   * standard payload shape into calls against this class's existing methods.
   *
   * `getCart` is intentionally left unconfigured — Shopify's docs state it is
   * not configurable (`.configure()` throws), and its default Storefront-API
   * read-only behavior does not conflict with anything this theme needs.
   *
   * Shopify injects its standard-actions bundle later in the document than this
   * module, so `Shopify.actions` is usually absent on the first attempt. This
   * method retries on an animation frame until the bundle attaches, up to
   * `#standardActionsRetryLimit` frames.
   *
   * A DOMContentLoaded fallback cannot serve here. This file loads as a module,
   * modules are deferred, and deferred scripts execute once parsing has finished.
   * `readyState` is therefore already past "loading" on the first attempt, so a
   * listener added then would never fire.
   * @private
   */
  #configureStandardActions() {
    if (typeof window.Shopify?.actions?.updateCart?.configure !== "function") {
      this.#standardActionsRetries += 1;

      if (this.#standardActionsRetries > this.#standardActionsRetryLimit) {
        console.warn(
          "CartStateManager: Shopify.actions never attached, so standard actions are unconfigured. " +
          "An app calling Shopify.actions.updateCart will use Shopify's default Storefront API path " +
          "instead of this theme's cart flow."
        );
        return;
      }

      window.requestAnimationFrame(() => this.#configureStandardActions());
      return;
    }

    window.Shopify.actions.updateCart.configure({
      eventTarget: (meta) => {
        if (this.allowDebug()) {
          console.log("CartStateManager: Shopify.actions.updateCart invoked", { meta });
        }

        if (meta.type === "shopify:cart:note-update" || meta.type === "shopify:cart:attributes-update") {
          return document.querySelector("cart-note") ?? document;
        }
        if (meta.type === "shopify:cart:discount-update") {
          return document.querySelector("cart-discounts") ?? document;
        }
        if (meta.type === "shopify:cart:lines-update" && meta.action === "add") {
          return document.querySelector("product-form") ?? document.querySelector("cart-items") ?? document;
        }
        return document.querySelector("cart-items") ?? document;
      },
      handler: (defaultHandler, payload, options) => this.#handleStandardUpdateCart(payload, options),
    });

    if (typeof window.Shopify?.actions?.openCart?.configure === "function") {
      window.Shopify.actions.openCart.configure({
        handler: () => {
          if (this.allowDebug()) {
            console.log("CartStateManager: Shopify.actions.openCart invoked");
          }

          // Shopify's default openCart looks for <cart-drawer-component>; this
          // theme's element is <cart-drawer>, so the default would silently
          // no-op without this override.
          const drawer = document.querySelector("cart-drawer");
          if (drawer?.open) {
            drawer.open();
          } else {
            window.location.href = window.theme?.routes?.cart_url || "/cart";
          }
        },
      });
    }

    if (this.allowDebug()) {
      console.log("CartStateManager: Standard storefront actions configured");
    }
  }

  /**
   * Handle a Shopify.actions.updateCart call by routing it through this theme's
   * existing Ajax Cart + cart-json enrichment methods.
   *
   * greyson:cart:* events for this mutation (changed / item:added / item:removed)
   * are NOT published inline as each sub-call completes. Every sub-call below is
   * given `{ publish: false }`, so it returns its event payload instead of firing
   * it, and this method collects those payloads and schedules them via
   * `queueMicrotask` after building its return value. The action runtime's own
   * `dispatchEvent` for shopify:cart:lines-update/note-update/discount-update
   * happens synchronously as soon as this method's returned promise resolves —
   * i.e. before that microtask runs — so greyson:cart:* reliably fires as a
   * CONSEQUENCE of the matching shopify:cart:* event, never ahead of it. See
   * utilities-events.js's header comment for the rationale.
   *
   * Known limitation: line updates/removals identify their target line via the
   * caller-supplied `id`, which this theme treats as its own Ajax cart `key`
   * (e.g. from `cart-item.js`'s `this.config.key`). There is no correlation
   * table between a Storefront API CartLine GID and this theme's Ajax `key`,
   * so an external caller that only knows a CartLine GID cannot resolve a line
   * today. Deferred until an external caller actually needs it.
   *
   * @param {Object} payload - Shopify.actions.updateCart payload ({ lines, note, discountCodes, attributes })
   * @param {Object} [options] - Options passed to the action call (signal, event.detail, event.context)
   * @returns {Promise<{cart: Object, userErrors: Array, warnings: Array, detail: Object}>}
   * @private
   */
  async #handleStandardUpdateCart(payload = {}, options = {}) {
    const { lines, note, discountCodes, attributes } = payload;

    // Collected as sub-calls complete, then published together after this
    // method returns (see the deferred-publish note above). Local to this call,
    // so overlapping invocations can never share or corrupt each other's data.
    let changePayload = null;
    let itemAddedPayload = null;
    let itemRemovedPayload = null;

    try {
      if (Array.isArray(lines) && lines.length > 0) {
        const addLines = lines.filter((line) => line.merchandiseId !== undefined);
        const mutateLines = lines.filter((line) => line.merchandiseId === undefined && line.id !== undefined);

        if (addLines.length > 0) {
          const items = addLines.map((line) => ({
            id: Number(String(line.merchandiseId).split("/").pop()),
            quantity: line.quantity,
            properties: line.attributes
              ? Object.fromEntries(line.attributes.map((attr) => [attr.key, attr.value]))
              : undefined,
          }));

          const openDrawer = options?.event?.detail?.openDrawer !== false;
          const addResult = await this.addItems(items, openDrawer, { publish: false });
          changePayload = addResult?.changePayload ?? changePayload;
          itemAddedPayload = addResult?.itemAddedPayload ?? itemAddedPayload;
        }

        if (mutateLines.length > 0) {
          const removeKeys = mutateLines.filter((line) => line.quantity === 0).map((line) => line.id);
          const updateLines = mutateLines.filter((line) => line.quantity !== 0);

          if (removeKeys.length > 0) {
            const removeResult = await this.removeItems(removeKeys, { publish: false });
            changePayload = removeResult?.changePayload ?? changePayload;
            itemRemovedPayload = removeResult?.itemRemovedPayload ?? itemRemovedPayload;
          }
          for (const line of updateLines) {
            const updateResult = await this.updateItem(line.id, line.quantity, { publish: false });
            changePayload = updateResult?.changePayload ?? changePayload;
          }
        }
      }

      if (note !== undefined) {
        const noteResult = await this.updateNote(note, { publish: false });
        changePayload = noteResult?.changePayload ?? changePayload;
      }

      if (discountCodes !== undefined) {
        const codes = Array.isArray(discountCodes) ? discountCodes.map((d) => d.code ?? d) : discountCodes;
        const discountResult = await this.applyDiscount(codes, { publish: false });
        changePayload = discountResult?.changePayload ?? changePayload;
      }

      if (attributes !== undefined) {
        // Storefront API shape is an array of { key, value } pairs; the theme's
        // /cart/update.js endpoint wants a plain object.
        const attributesObject = Array.isArray(attributes)
          ? Object.fromEntries(attributes.map((attr) => [attr.key, attr.value]))
          : attributes;
        const attributesResult = await this.updateAttributes(attributesObject, { publish: false });
        changePayload = attributesResult?.changePayload ?? changePayload;
      }

      const result = {
        cart: this.#buildStorefrontCartShape(),
        userErrors: [],
        warnings: [],
        detail: { source: "cart-state-manager" },
      };

      if (this.allowDebug()) {
        console.log("CartStateManager: standard updateCart action handled", { result });
      }

      queueMicrotask(() => {
        // Order doesn't matter to any current subscriber, but item:added/removed
        // before the generic changed event mirrors this class's own historical
        // publish order.
        if (itemAddedPayload) this._publish(EVENTS.CART_STATE_ITEM_ADDED, itemAddedPayload);
        if (itemRemovedPayload) this._publish(EVENTS.CART_STATE_ITEM_REMOVED, itemRemovedPayload);
        if (changePayload) this._publish(EVENTS.CART_STATE_CHANGED, changePayload, { sticky: true });
      });

      return result;
    } catch (error) {
      // Rethrow so the action runtime auto-emits shopify:cart:error. Resolving
      // with userErrors instead would report the failure only to the caller,
      // leaving every shopify:cart:error listener silent. Shopify reserves
      // userErrors for a mutation the cart rejected while still responding;
      // a thrown Ajax Cart error means the request itself did not succeed.
      if (this.allowDebug()) {
        console.warn("CartStateManager: standard updateCart handler failed", { error });
      }

      // No greyson:cart:error here: shopify:cart:error already covers this path
      // (it's what the rethrow below causes the action runtime to emit), and
      // nothing subscribes to a second, theme-only failure event for a path that
      // already has a stable standard one. cart-note.js's own catch blocks still
      // publish greyson:cart:error directly alongside this, for its existing
      // theme-internal subscribers.
      throw error;
    }
  }

  /**
   * Convert the enriched cart state into the Storefront API cart shape that
   * Shopify's action runtime requires from a configured handler.
   *
   * The runtime reads `cart.lines.nodes` from the handler's return value without
   * checking whether it exists, and then flattens it to `cart.lines`. Returning
   * the theme's own state shape, whose line items live on `items`, therefore
   * throws inside the runtime after the mutation has already succeeded. That
   * surfaces a spurious shopify:cart:error and rejects the caller's promise even
   * though the cart changed.
   *
   * Money values convert from the theme's cents to MoneyV2 major units.
   * @returns {Object} Cart in the Storefront API shape
   * @private
   */
  #buildStorefrontCartShape() {
    const state = this.state;
    const currencyCode = state.currency || "USD";
    const money = (cents) => ({
      amount: ((Number(cents) || 0) / 100).toFixed(2),
      currencyCode,
    });

    return {
      id: null,
      totalQuantity: state.itemCount || 0,
      cost: { totalAmount: money(state.totalPrice) },
      lines: {
        nodes: (state.items || []).map((item) => ({
          id: item.key,
          quantity: item.quantity,
          cost: { totalAmount: money(item.finalLinePrice) },
          merchandise: {
            id: item.variantId,
            product: { id: item.productId },
          },
        })),
      },
      discountCodes: (state.discountApplications || []).map((discount) => ({
        code: discount.title,
        applicable: true,
      })),
      note: state.note || null,
      // Storefront API shape: an array of { key, value } pairs, not a plain
      // object — matches what #handleStandardUpdateCart accepts on the way in.
      attributes: Object.entries(state.attributes || {}).map(([key, value]) => ({ key, value })),
    };
  }

  /**
   * Check if cart drawer is ready and dispatch greyson:cart:system:ready event
   * @private
   */
  _checkAndDispatchCartSystemReady() {
    if (this.drawer) {
      this._publish(EVENTS.CART_SYSTEM_READY, {
        cartStateManager: this,
        cartDrawer: this.drawer,
      }, { sticky: true });

      if (this.allowDebug()) {
        console.log(
          "CartStateManager: Cart system fully ready - both state manager and cart drawer available"
        );
      }
    } else {
      if (this.allowDebug()) {
        console.log(
          "CartStateManager: Cart state manager ready, waiting for cart drawer"
        );
      }
    }
  }

  /**
   * Refresh cart state from cart-json section
   * @param {Object} options - Options for the refresh
   * @param {boolean} options.force - Force UI update even if no changes detected
   * @param {string} options.source - Source of the refresh request for debugging
   */
  async refreshState(options = {}) {
    const { force = false, source = 'unknown' } = options;

    if (this.allowDebug()) {
      console.log("CartStateManager: Refreshing cart state from cart-json section", {
        force,
        source
      });
    }

    try {
      // Fetch enriched cart data from cart-json section
      const enrichedCartData = await this.#fetchEnrichedCartData();

      if (!enrichedCartData) {
        throw new Error("Failed to fetch enriched cart data");
      }

      if (this.allowDebug()) {
        console.log("CartStateManager: Enriched cart data fetched", {
          itemCount: enrichedCartData.itemCount,
          totalPrice: enrichedCartData.totalPrice,
          totalDiscount: enrichedCartData.totalDiscount,
          itemsLength: enrichedCartData.items?.length || 0,
          source,
        });
      }

      // Update state with enriched data (but don't dispatch events yet)
      this._updateState(enrichedCartData, { force, source, suppressEvents: true });

      // Now dispatch the state change event
      this.#dispatchStateChangeEvent(enrichedCartData, { force, source });

      return this.state;
    } catch (error) {
      console.error("CartStateManager: Error refreshing cart state:", error);
      throw error;
    }
  }

  /**
   * Update the internal state and optionally emit change event
   * @param {Object} newState - New cart state data from cart-json section
   * @param {Object} options - Options for the update
   * @param {boolean} options.force - Force event dispatch even if no changes detected
   * @param {string} options.source - Source of the update for debugging
   * @param {boolean} options.suppressEvents - Don't dispatch events (used when fetching enriched data first)
   * @private
   */
  _updateState(newState, options = {}) {
    const { force = false, source = 'unknown', suppressEvents = false } = options;
    const previousState = { ...this.state };

    // Directly use the enriched cart data (already in camelCase from cart-json section)
    this.state = {
      ...this.state,
      ...newState,
      items: Array.isArray(newState.items) ? newState.items : this.state.items,
    };

    // Store changes for later use if events are suppressed
    this._pendingChanges = this._getStateChanges(previousState, this.state);
    this._previousState = previousState;

    if (!suppressEvents) {
      this.#dispatchStateChangeEvent(newState, { force, source });
    }
  }

  /**
   * Dispatch state change event with current state and changes
   *
   * `publish: false` is used by the mutation methods that run inside
   * #handleStandardUpdateCart (addItems, removeItems, updateCart, etc.): rather
   * than publishing CART_STATE_CHANGED immediately, this computes the payload and
   * returns it so the caller can carry it up to #handleStandardUpdateCart, which
   * defers the actual publish until after Shopify's own shopify:cart:* standard
   * event has fired (see that method for why). Direct callers — refreshState,
   * the visibility-change listener, and the network observer — keep the default
   * (`publish: true`) and are unaffected, since none of those paths has a
   * shopify:cart:* equivalent to defer to.
   *
   * @param {Object} originalNewState - The original new state data that triggered the update
   * @param {Object} options - Options for the event dispatch
   * @param {boolean} options.force - Force event dispatch even if no changes detected
   * @param {string} options.source - Source of the update for debugging
   * @param {boolean} [options.publish=true] - When false, return the payload instead of publishing it
   * @returns {Object|null} The CART_STATE_CHANGED payload when publish is false and there were
   *   changes (or force was set); otherwise null. Ignored when publish is true (the default).
   * @private
   */
  #dispatchStateChangeEvent(originalNewState, options = {}) {
    const { force = false, source = 'unknown', publish = true } = options;
    const changes = this._pendingChanges || {};
    const previousState = this._previousState || {};
    const hasChanges = Object.keys(changes).length > 0;

    let changePayload = null;

    // Only emit state change event if there are actual changes or if forced
    if (hasChanges || force) {
      changePayload = {
        state: this.state,
        previousState,
        changes: changes,
        source,
        forced: force && !hasChanges
      };

      if (publish) {
        this._publish(EVENTS.CART_STATE_CHANGED, changePayload, { sticky: true });
      }

      // Auto-update all cart count elements if item count changed
      if (changes.itemCount) {
        this.updateAllCartCountElements();
      }
    }

    // Mark initial state as loaded
    if (!this.initialStateLoaded) {
      this.initialStateLoaded = true;
      // No shopify:* equivalent exists for "this is the first load", so this
      // always self-publishes immediately regardless of `publish`.
      this._publish(EVENTS.CART_STATE_INITIAL_LOAD, { state: this.state, source }, { sticky: true });
    }

    // Clear pending changes
    this._pendingChanges = null;
    this._previousState = null;

    return changePayload;
  }

  /**
   * Get the differences between two states
   * @param {Object} previousState - Previous state
   * @param {Object} currentState - Current state
   * @returns {Object} Object describing the changes
   * @private
   */
  _getStateChanges(previousState, currentState) {
    const changes = {};

    // Check for item count changes
    if (previousState.itemCount !== currentState.itemCount) {
      changes.itemCount = {
        from: previousState.itemCount,
        to: currentState.itemCount,
      };
    }

    // Check for total price changes
    if (previousState.totalPrice !== currentState.totalPrice) {
      changes.totalPrice = {
        from: previousState.totalPrice,
        to: currentState.totalPrice,
      };
    }

    // Check for total price changes
    if (previousState.totalPriceMoney !== currentState.totalPriceMoney) {
      changes.totalPriceMoney = {
        from: previousState.totalPriceMoney,
        to: currentState.totalPriceMoney,
      };
    }

    // Check for discount changes
    if (previousState.totalDiscount !== currentState.totalDiscount) {
      changes.totalDiscount = {
        from: previousState.totalDiscount,
        to: currentState.totalDiscount,
      };
    }

    // Check for note changes. Needed so a note-only mutation (e.g. via
    // updateNote or the standard shopify:cart:note-update action path) is
    // recognized as a change — otherwise #dispatchStateChangeEvent sees an
    // empty `changes` object and never publishes greyson:cart:changed.
    if (previousState.note !== currentState.note) {
      changes.note = {
        from: previousState.note,
        to: currentState.note,
      };
    }

    // Check for attribute changes. Same rationale as note above, for
    // updateAttributes / shopify:cart:attributes-update.
    const previousAttributes = previousState.attributes || {};
    const currentAttributes = currentState.attributes || {};
    if (JSON.stringify(previousAttributes) !== JSON.stringify(currentAttributes)) {
      changes.attributes = {
        from: previousAttributes,
        to: currentAttributes,
      };
    }

    // Check for discount applications changes
    const previousDiscounts = previousState.discountApplications || [];
    const currentDiscounts = currentState.discountApplications || [];
    if (
      JSON.stringify(previousDiscounts) !== JSON.stringify(currentDiscounts)
    ) {
      changes.discountApplications = {
        from: previousDiscounts,
        to: currentDiscounts,
      };

      // Determine what changed with discounts (using unique titles to avoid duplicates)
      const previousDiscountTitles = new Set(
        previousDiscounts.map((d) => d.title)
      );
      const currentDiscountTitles = new Set(
        currentDiscounts.map((d) => d.title)
      );

      if (this.allowDebug()) {
        console.log("CartStateManager: Analyzing discount changes", {
          previousTitles: [...previousDiscountTitles],
          currentTitles: [...currentDiscountTitles],
          previousCount: previousDiscounts.length,
          currentCount: currentDiscounts.length,
        });
      }

      // Check for added discounts
      const addedDiscounts = [...currentDiscountTitles].filter(
        (title) => !previousDiscountTitles.has(title)
      );
      if (addedDiscounts.length > 0) {
        changes.discountsAdded = addedDiscounts;
        if (this.allowDebug()) {
          console.log(
            "CartStateManager: Discounts added detected",
            addedDiscounts
          );
        }
      }

      // Check for removed discounts
      const removedDiscounts = [...previousDiscountTitles].filter(
        (title) => !currentDiscountTitles.has(title)
      );
      if (removedDiscounts.length > 0) {
        changes.discountsRemoved = removedDiscounts;
        if (this.allowDebug()) {
          console.log(
            "CartStateManager: Discounts removed detected",
            removedDiscounts
          );
        }
      }
    }

    // Check for items changes with detailed analysis
    const previousItems = previousState.items || [];
    const currentItems = currentState.items || [];

    if (JSON.stringify(previousItems) !== JSON.stringify(currentItems)) {
      changes.items = {
        from: previousItems,
        to: currentItems,
      };

      // Determine the type of items change
      const previousItemKeys = new Set(previousItems.map((item) => item.key));
      const currentItemKeys = new Set(currentItems.map((item) => item.key));

      // Check for added items
      const addedKeys = [...currentItemKeys].filter(
        (key) => !previousItemKeys.has(key)
      );
      if (addedKeys.length > 0) {
        changes.itemsAdded = addedKeys;
        if (this.allowDebug()) {
          console.log("CartStateManager: Items added detected", addedKeys);
        }
      }

      // Check for removed items
      const removedKeys = [...previousItemKeys].filter(
        (key) => !currentItemKeys.has(key)
      );
      if (removedKeys.length > 0) {
        changes.itemsRemoved = removedKeys;
        if (this.allowDebug()) {
          console.log("CartStateManager: Items removed detected", removedKeys);
        }
      }

      // Check for updated items (same key but different content)
      const commonKeys = [...currentItemKeys].filter((key) =>
        previousItemKeys.has(key)
      );
      const updatedKeys = commonKeys.filter((key) => {
        const prevItem = previousItems.find((item) => item.key === key);
        const currItem = currentItems.find((item) => item.key === key);
        return JSON.stringify(prevItem) !== JSON.stringify(currItem);
      });

      if (updatedKeys.length > 0) {
        changes.itemsUpdated = updatedKeys;
        if (this.allowDebug()) {
          console.log("CartStateManager: Items updated detected", updatedKeys);
        }
      }
    }

    return changes;
  }

  /**
   * Add item(s) to cart using Shopify Cart API
   *
   * Convenience method that accepts either a single item or array of items.
   * Automatically tracks the source component and dispatches relevant events.
   *
   * @param {Object|Array} items - Item or array of items to add to cart
   * @param {number} items.id - Variant ID of the product
   * @param {number} items.quantity - Quantity to add
   * @param {Object} [items.properties] - Line item properties
   * @param {boolean} [openDrawer=true] - Whether to open cart drawer after adding
   * @returns {Promise<Object>} Promise resolving to operation result
   * @throws {Error} If cart system is not ready or API call fails
   * @fires greyson:cart:item:adding - Before attempting to add items
   * @fires greyson:cart:item:added - After successfully adding items, once
   *   Shopify's shopify:cart:lines-update has fired (when routed through the
   *   standard action — see #handleStandardUpdateCart)
   * @fires greyson:cart:changed - When cart state updates, same timing as above
   *
   * @example
   * // Add single item
   * await cartStateManager.addItem({
   *   id: 123456789,
   *   quantity: 1,
   *   properties: { gift_message: 'Happy Birthday!' }
   * });
   *
   * @example
   * // Add multiple items
   * await cartStateManager.addItem([
   *   { id: 123, quantity: 1 },
   *   { id: 456, quantity: 2 }
   * ], false); // Don't open drawer
   */
  async addItem(items, openDrawer = true) {
    // Proxy to addItems for consistency
    const itemsArray = Array.isArray(items) ? items : [items];
    return this.addItems(itemsArray, openDrawer);
  }

  /**
   * Add multiple items to cart using Shopify's add.js endpoint
   *
   * Core method for adding items to cart. Handles both single and bulk additions,
   * manages loading states, and ensures proper event dispatching throughout the process.
   * Automatically adds component tracking properties to items.
   *
   * @param {Array} items - Array of cart items to add
   * @param {number} items[].id - Variant ID of the product
   * @param {number} items[].quantity - Quantity to add
   * @param {Object} [items[].properties] - Line item properties
   * @param {boolean} [openDrawer=true] - Whether to open cart drawer after adding
   * @returns {Promise<Object>} Promise resolving to add result and derived line items
   * @throws {Error} If operation is blocked by concurrent update or API call fails
   * @fires greyson:cart:item:adding - Before attempting to add items
   * @fires greyson:cart:item:added - After successfully adding items, deferred
   *   until after shopify:cart:lines-update fires when called via the standard
   *   action (pass `{ publish: false }` to suppress this method's own publish,
   *   as #handleStandardUpdateCart does)
   * @fires greyson:cart:changed - When cart state updates, same timing as above
   *
   * If addition fails when called via the standard action, no greyson:cart:error
   * fires — the rethrow causes Shopify's runtime to emit shopify:cart:error
   * instead, which already covers this path.
   *
   * @example
   * // Add membership bundle
   * await cartStateManager.addItems([
   *   { id: 123, quantity: 1, properties: { _gc_bundle_id: 'mens-membership' }},
   *   { id: 456, quantity: 1, properties: { _gc_bundle_id: 'mens-membership' }}
   * ]);
   */
  async addItems(items, openDrawer = true, options = {}) {
    const { publish = true } = options;

    if (this.isUpdating) {
      if (this.allowDebug()) {
        console.log("CartStateManager: Add items blocked - already updating");
      }
      return;
    }

    const itemsArray = Array.isArray(items) ? items : [items];
    const previousItems = Array.isArray(this.state.items) ? [...this.state.items] : [];

    itemsArray.forEach((item) => {
      if (!item.properties) {
        item.properties = {};
      }
      if (!item.properties.hasOwnProperty("_add_to_cart_component")) {
        item.properties["_add_to_cart_component"] = "unknown";
      }
    });

    const requestItems = itemsArray.map((item) => sanitizeCartAddItem(item));

    if (this.allowDebug()) {
      console.log("CartStateManager: Adding items to cart", {
        items: itemsArray,
        itemCount: itemsArray.length,
        openDrawer: openDrawer,
        currentItemCount: this.state.itemCount,
      });
    }

    this.isUpdating = true;

    try {
      this._publish(EVENTS.CART_STATE_ITEM_ADDING, { items: itemsArray, openDrawer });

      // Add one line per request, rather than one request carrying an items array.
      //
      // Shopify's Web Pixel layer reports a single-item add.js body as
      // product_added_to_cart, but an { items: [...] } body produces no customer
      // event, so a multi-line add such as a bundle or a CYO build goes
      // unreported. Sequential single-item requests keep every line observable.
      //
      // Requests run in sequence because each response supersedes the previous
      // cart state, and the last response feeds the enrichment step below.
      //
      // No bundled sections in the body, which keeps each payload light. Enriched
      // cart state is fetched once, after the last add succeeds.
      //
      // allowBulkAdd() is a manual testing escape hatch only (see its doc) — it
      // trades away the pixel-visibility guarantee above, so it must never be on
      // by default in production.
      const addResponses = [];

      if (this.allowBulkAdd() && requestItems.length > 1) {
        if (this.allowDebug()) {
          console.log("CartStateManager: Bulk add mode active, posting one batched request", {
            itemCount: requestItems.length,
          });
        }

        const response = await this._postCartMutation(
          `${window.theme?.routes?.cart_add_url}.js`,
          { items: requestItems }
        );

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || "Failed to add items to cart");
        }

        addResponses.push(await response.json());
      } else {
        for (const requestItem of requestItems) {
          const response = await this._postCartMutation(
            `${window.theme?.routes?.cart_add_url}.js`,
            requestItem
          );

          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || "Failed to add items to cart");
          }

          addResponses.push(await response.json());
        }
      }

      if (!addResponses.length) {
        throw new Error("Failed to add items to cart: no items were provided");
      }

      // Every line the loop added, in one response-shaped object. Each single-item
      // add.js response describes one line, so collecting them keeps the added-line
      // list complete for analytics rather than reporting only the last request.
      // In bulk mode, the single batched response already carries every added
      // line under its own `items` array, so it's used as-is.
      const responseData = addResponses.length === 1
        ? addResponses[0]
        : { ...addResponses[addResponses.length - 1], items: addResponses };

      if (this.allowDebug()) {
        console.log(
          "CartStateManager: Items added successfully, response received",
          {
            hasCartData: !!responseData,
            responseKeys: Object.keys(responseData),
            hasSections: !!responseData.sections,
            responseDataStructure: responseData
          }
        );
      }

      // Parse enriched cart data from bundled response
      let enrichedCartData = null;

      if (responseData.sections && responseData.sections["cart-json"]) {
        try {
          enrichedCartData = this.#parseEnrichedCartData(responseData.sections);

          if (this.allowDebug()) {
            console.log("CartStateManager: Enriched data parsed from bundled addItems response");
          }
        } catch (error) {
          console.warn("CartStateManager: Failed to parse bundled enriched cart data:", error);
        }
      } else {
        if (this.allowDebug()) {
          console.log("CartStateManager: Fetching enriched cart data after addItems (fallback)");
        }
        try {
          enrichedCartData = await this.#fetchEnrichedCartData();
        } catch (error) {
          console.error("CartStateManager: Failed to fetch enriched cart data:", error);
          throw error;
        }
      }

      // Update state with the enriched cart data (but don't dispatch events yet)
      this._updateState(enrichedCartData, { suppressEvents: true });

      // Now dispatch the state change event with fresh data available
      const changePayload = this.#dispatchStateChangeEvent(enrichedCartData, { source: 'addItems', publish });

      const addedLineItems = this.#resolveAddedLineItems(
        responseData,
        previousItems,
        this.state.items,
        itemsArray
      );

      const itemAddedPayload = {
        items: itemsArray,
        openDrawer,
        state: this.state,
        addedLineItems,
        addResponse: responseData,
      };

      if (publish) {
        this._publish(EVENTS.CART_STATE_ITEM_ADDED, itemAddedPayload);
      }

      if (this.allowDebug()) {
        console.log("CartStateManager: Add items complete", {
          newItemCount: this.state.itemCount,
          itemsAdded: itemsArray.length,
          addedLineItemsCount: addedLineItems.length,
        });
      }

      return {
        success: true,
        items: itemsArray,
        addedLineItems,
        addResponse: responseData,
        changePayload,
        itemAddedPayload,
      };
    } catch (error) {
      console.error("CartStateManager: Error adding items to cart:", error);
      throw error;
    } finally {
      this.isUpdating = false;
    }
  }

  /**
   * Resolve canonical added line items from add.js response.
   * Falls back to cart state diffing when Shopify responds with sections-only payloads.
   * @param {Object|null} responseData
   * @param {Array} previousItems
   * @param {Array} currentItems
   * @param {Array} requestedItems
   * @returns {Array}
   * @private
   */
  #resolveAddedLineItems(responseData, previousItems, currentItems, requestedItems) {
    if (Array.isArray(responseData?.items) && responseData.items.length > 0) {
      return responseData.items;
    }

    if (responseData?.id && responseData?.key) {
      return [responseData];
    }

    const previousKeys = new Set(
      (Array.isArray(previousItems) ? previousItems : [])
        .map((item) => item?.key)
        .filter(Boolean)
    );
    const currentItemsArray = Array.isArray(currentItems) ? currentItems : [];
    const addedByNewKey = currentItemsArray.filter(
      (item) => item?.key && !previousKeys.has(item.key)
    );

    if (addedByNewKey.length > 0) {
      return addedByNewKey;
    }

    const requestedVariantIds = new Set(
      (Array.isArray(requestedItems) ? requestedItems : [])
        .map((item) => Number(item?.id))
        .filter((id) => Number.isFinite(id))
    );

    if (requestedVariantIds.size === 0) {
      return [];
    }

    const previousQtyByVariantId = new Map();
    for (const item of Array.isArray(previousItems) ? previousItems : []) {
      const variantId = Number(item?.variantId || item?.id);
      if (!Number.isFinite(variantId)) continue;
      const quantity = Number(item?.quantity) || 0;
      previousQtyByVariantId.set(
        variantId,
        (previousQtyByVariantId.get(variantId) || 0) + quantity
      );
    }

    const addedByQuantity = [];
    for (const item of currentItemsArray) {
      const variantId = Number(item?.variantId || item?.id);
      if (!Number.isFinite(variantId) || !requestedVariantIds.has(variantId)) {
        continue;
      }
      const previousQuantity = previousQtyByVariantId.get(variantId) || 0;
      const currentQuantity = Number(item?.quantity) || 0;
      if (currentQuantity > previousQuantity) {
        addedByQuantity.push(item);
      }
    }

    return addedByQuantity;
  }

  /**
   * Set one cart line's quantity.
   *
   * The endpoint depends on the direction of the change, because Shopify's Web
   * Pixel layer reports the two differently. An increase through update.js reports
   * product_added_to_cart. A decrease through the same endpoint reports nothing, so
   * a reduction goes to the line-scoped change.js, which reports the quantity the
   * customer gave up. A quantity of 0 removes the line and routes through
   * removeItem for the same reason.
   *
   * @param {string} key - Cart item key
   * @param {number} quantity - New quantity
   * @param {Object} [options]
   * @param {boolean} [options.publish=true] - Forwarded to whichever underlying
   *   method handles this change; see updateCart's `publish` doc.
   * @returns {Promise<Object|undefined>} Result of the underlying operation
   */
  async updateItem(key, quantity, options = {}) {
    const { publish = true } = options;
    const newQuantity = Number(quantity) || 0;

    if (newQuantity <= 0) {
      return this.removeItems([key], { publish });
    }

    const currentQuantity = Number(this.getItem(key)?.quantity) || 0;

    if (newQuantity < currentQuantity) {
      return this.#changeLineQuantity(key, newQuantity, { publish });
    }

    return this.updateCart({ updates: { [key]: newQuantity }, publish });
  }

  /**
   * Set one cart line's quantity through the line-scoped change.js endpoint.
   *
   * Mirrors updateCart's flow: publish, mutate, refresh enriched state, then
   * dispatch. It exists because update.js cannot express a reduction that
   * Shopify's Web Pixel layer will report.
   *
   * @param {string} key - Cart item key
   * @param {number} quantity - New quantity, above zero
   * @param {Object} [options]
   * @param {boolean} [options.publish=true] - Forwarded to #dispatchStateChangeEvent.
   * @returns {Promise<Object>} Result flag and the applied quantity
   * @private
   */
  async #changeLineQuantity(key, quantity, options = {}) {
    const { publish = true } = options;

    if (this.isUpdating) {
      if (this.allowDebug()) {
        console.log("CartStateManager: Change line quantity blocked - already updating");
      }
      return;
    }

    this.isUpdating = true;

    try {
      const response = await this._postCartMutation(
        `${window.theme?.routes?.cart_change_url}.js`,
        { id: key, quantity }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to change line quantity");
      }

      await response.json();

      const enrichedCartData = await this.#fetchEnrichedCartData();

      this._updateState(enrichedCartData, { suppressEvents: true });
      const changePayload = this.#dispatchStateChangeEvent(enrichedCartData, { source: "changeLineQuantity", publish });

      if (this.allowDebug()) {
        console.log("CartStateManager: Change line quantity complete", {
          key,
          quantity,
          newItemCount: this.state.itemCount,
        });
      }

      return { success: true, key, quantity, changePayload };
    } catch (error) {
      console.error("CartStateManager: Error changing line quantity:", error);
      throw error;
    } finally {
      this.isUpdating = false;
    }
  }

  /**
   * Update multiple items quantities in cart (proxy to updateCart)
   * @param {Object} updates - Object mapping line item keys to quantities
   */
  async updateItems(updates) {
    return this.updateCart({ updates });
  }

  /**
   * Comprehensive cart update using update.js endpoint
   * @param {Object} options - Update options
   * @param {Object} options.updates - Line item quantity updates { key: quantity }
   * @param {string} options.note - Cart note
   * @param {Object} options.attributes - Cart attributes { key: value }
   * @param {string} options.discount - Discount code(s), comma-separated for multiple
   * @param {boolean} [options.publish=true] - Forwarded to #dispatchStateChangeEvent.
   *   #handleStandardUpdateCart passes false so CART_STATE_CHANGED is deferred until
   *   after the matching shopify:cart:* event fires — including for attributes writes
   *   now that cart-note.js routes through the standard action too. Direct callers
   *   that bypass the standard action entirely (refreshState, the visibility-change
   *   listener, the network observer) keep the default so they still self-publish
   *   immediately.
   */
  async updateCart(options = {}) {
    if (this.isUpdating) {
      if (this.allowDebug()) {
        console.log("CartStateManager: Update cart blocked - already updating");
      }
      return;
    }

    const { updates, note, attributes, discount, publish = true } = options;

    if (this.allowDebug()) {
      console.log("CartStateManager: Updating cart", {
        updates: updates,
        note: note,
        attributes: attributes,
        discount: discount,
        updateCount: updates ? Object.keys(updates).length : 0,
        currentItemCount: this.state.itemCount,
      });
    }

    this.isUpdating = true;

    try {

      // Build request body without bundled sections to keep update.js payload lightweight.
      // Enriched cart state is fetched immediately after update succeeds.
      const requestBody = {};
      if (updates) requestBody.updates = updates;
      if (note !== undefined) requestBody.note = note;
      if (attributes) requestBody.attributes = attributes;
      if (discount !== undefined) requestBody.discount = discount;

      const response = await this._postCartMutation(
        `${window.theme?.routes?.cart_update_url}.js`,
        requestBody
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to update cart");
      }

      const responseData = await response.json();

      if (this.allowDebug()) {
        console.log(
          "CartStateManager: Cart updated successfully",
          {
            hasResponseData: !!responseData,
            hasSections: !!responseData.sections
          }
        );
      }

      // Parse enriched cart data from bundled response
      let enrichedCartData = null;

      if (responseData.sections && responseData.sections["cart-json"]) {
        try {
          enrichedCartData = this.#parseEnrichedCartData(responseData.sections);
          if (this.allowDebug()) {
            console.log("CartStateManager: Parsed enriched cart data from bundled updateCart response");
          }
        } catch (error) {
          console.warn("CartStateManager: Failed to parse bundled enriched cart data:", error);
        }
      }

      // Fall back to fetching if bundled parsing failed
      if (!enrichedCartData) {
        if (this.allowDebug()) {
          console.log("CartStateManager: Fetching enriched cart data after updateCart (fallback)");
        }
        try {
          enrichedCartData = await this.#fetchEnrichedCartData();
        } catch (error) {
          console.error("CartStateManager: Failed to fetch enriched cart data:", error);
          throw error;
        }
      }

      // Update state with enriched cart data (but don't dispatch events yet)
      this._updateState(enrichedCartData, { suppressEvents: true });

      // Now dispatch the state change event with fresh enriched data available
      const changePayload = this.#dispatchStateChangeEvent(enrichedCartData, { source: 'updateCart', publish });

      if (this.allowDebug()) {
        console.log("CartStateManager: Update cart complete", {
          newItemCount: this.state.itemCount,
          updatedItems: updates ? Object.keys(updates).length : 0,
          noteUpdated: note !== undefined,
          attributesUpdated: !!attributes,
          discountUpdated: discount !== undefined,
        });
      }

      return { success: true, updates, note, attributes, discount, changePayload };
    } catch (error) {
      console.error("CartStateManager: Error updating cart:", error);
      throw error;
    } finally {
      this.isUpdating = false;
    }
  }

  /**
   * Remove item from cart (proxy to removeItems)
   * @param {string} key - Cart item key
   */
  async removeItem(key) {
    return this.removeItems([key]);
  }

  /**
   * Remove multiple items from cart using update.js endpoint
   * @param {Array<string>} keys - Array of cart item keys to remove
   */
  async removeItems(keys, options = {}) {
    const { publish = true } = options;

    if (this.isUpdating) {
      if (this.allowDebug()) {
        console.log(
          "CartStateManager: Remove items blocked - already updating"
        );
      }
      return;
    }

    if (this.allowDebug()) {
      console.log("CartStateManager: Removing multiple items from cart", {
        keys: keys,
        itemCount: keys.length,
      });
    }

    this.isUpdating = true;

    try {
      // Get items before removal for the event
      const itemsBeingRemoved = keys
        .map((key) => this.getItem(key))
        .filter(Boolean);


      // Remove through change.js, one request per line.
      //
      // update.js with a quantity of 0 empties the line, but Shopify reads that
      // request as a quantity update and its Web Pixel layer emits nothing. A
      // quantity increase through the same endpoint does emit
      // product_added_to_cart, so a removal would otherwise be the one cart
      // operation that customer-event pixels never observe.
      //
      // change.js is line-scoped, so it takes one line per request and reports a
      // removal that pixels recognise. Requests run in sequence because each
      // response supersedes the previous cart state.
      let responseData = null;

      for (const key of keys) {
        const response = await this._postCartMutation(
          `${window.theme?.routes?.cart_change_url}.js`,
          { id: key, quantity: 0 }
        );

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || "Failed to remove items");
        }

        responseData = await response.json();
      }

      if (!responseData) {
        throw new Error("Failed to remove items: no lines were provided");
      }

      if (this.allowDebug()) {
        console.log(
          "CartStateManager: Items removed successfully",
          {
            hasResponseData: !!responseData,
            hasSections: !!responseData.sections
          }
        );
      }

      // Parse enriched cart data from bundled response
      let enrichedCartData = null;

      if (responseData.sections && responseData.sections["cart-json"]) {
        try {
          enrichedCartData = this.#parseEnrichedCartData(responseData.sections);
          if (this.allowDebug()) {
            console.log("CartStateManager: Parsed enriched cart data from bundled removeItems response");
          }
        } catch (error) {
          console.warn("CartStateManager: Failed to parse bundled enriched cart data:", error);
        }
      }

      // Fall back to fetching if bundled parsing failed
      if (!enrichedCartData) {
        if (this.allowDebug()) {
          console.log("CartStateManager: Fetching enriched cart data after removeItems (fallback)");
        }
        try {
          enrichedCartData = await this.#fetchEnrichedCartData();
        } catch (error) {
          console.error("CartStateManager: Failed to fetch enriched cart data:", error);
          throw error;
        }
      }

      // Update state with enriched cart data (but don't dispatch events yet)
      this._updateState(enrichedCartData, { suppressEvents: true });

      // Now dispatch the state change event with fresh enriched data available
      const changePayload = this.#dispatchStateChangeEvent(enrichedCartData, { source: 'removeItems', publish });

      const itemRemovedPayload = { keys, items: itemsBeingRemoved, state: this.state };

      if (publish) {
        this._publish(EVENTS.CART_STATE_ITEM_REMOVED, itemRemovedPayload);
      }

      if (this.allowDebug()) {
        console.log("CartStateManager: Remove items complete", {
          newItemCount: this.state.itemCount,
          removedItems: keys.length,
        });
      }

      return { success: true, removedItems: itemsBeingRemoved, changePayload, itemRemovedPayload };
    } catch (error) {
      console.error("CartStateManager: Error removing items from cart:", error);
      throw error;
    } finally {
      this.isUpdating = false;
    }
  }

  /**
   * Get current cart state
   * @returns {Object} Current cart state
   */
  getState() {
    return { ...this.state };
  }

  /**
   * Get specific cart item by key
   * @param {string} key - Cart item key
   * @returns {Object|null} Cart item or null if not found
   */
  getItem(key) {
    return this.state.items.find((item) => item.key === key) || null;
  }

  /**
   * Get all cart items
   * @returns {Array} Array of cart items
   */
  getItems() {
    return [...this.state.items];
  }

  /**
   * Check if cart is empty
   * @returns {boolean} True if cart is empty
   */
  isEmpty() {
    return this.state.itemCount === 0;
  }

  /**
   * Get formatted total price
   * @returns {string} Formatted total price
   */
  getFormattedTotal() {
    return this.state.totalPriceMoney;
  }

  /**
   * Update cart attributes (convenience method)
   * @param {Object} attributes - Cart attributes to update
   * @param {Object} [options]
   * @param {boolean} [options.publish=true] - Forwarded to updateCart.
   */
  async updateAttributes(attributes, options = {}) {
    const { publish = true } = options;

    if (this.allowDebug()) {
      console.log("CartStateManager: Updating cart attributes", {
        attributes: attributes,
        currentAttributes: this.state.attributes,
      });
    }

    return this.updateCart({ attributes, publish });
  }

  /**
   * Update cart note (convenience method)
   * @param {string} note - New cart note
   * @param {Object} [options]
   * @param {boolean} [options.publish=true] - Forwarded to updateCart.
   */
  async updateNote(note, options = {}) {
    const { publish = true } = options;

    if (this.allowDebug()) {
      console.log("CartStateManager: Updating cart note", {
        note: note,
        currentNote: this.state.note,
      });
    }

    return this.updateCart({ note, publish });
  }

  /**
   * Apply discount code(s) to cart (convenience method)
   * @param {string|Array<string>} discounts - Discount code(s) to apply
   * @param {Object} [options]
   * @param {boolean} [options.publish=true] - Forwarded to updateCart.
   */
  async applyDiscount(discounts, options = {}) {
    const { publish = true } = options;
    const discountString = Array.isArray(discounts)
      ? discounts.join(",")
      : discounts;

    if (this.allowDebug()) {
      console.log("CartStateManager: Applying discount codes", {
        discounts: discountString,
        currentDiscounts: this.state.cartLevelDiscountApplications,
      });
    }

    return this.updateCart({ discount: discountString, publish });
  }

  /**
   * Remove all discount codes from cart (convenience method)
   */
  async removeDiscounts() {
    if (this.allowDebug()) {
      console.log("CartStateManager: Removing all discount codes");
    }

    return this.updateCart({ discount: "" });
  }

  /**
   * Parse enriched cart data from bundled section response
   * @param {Object} sections - Sections object from cart API response
   * @returns {Object|null} Parsed enriched cart data or null if parsing failed
   * @private
   */
  #parseEnrichedCartData(sections) {
    if (!sections || !sections["cart-json"]) {
      if (this.allowDebug()) {
        console.log("CartStateManager: No cart-json section in bundled response", {
          sections: sections,
          availableSections: sections ? Object.keys(sections) : []
        });
      }
      return null;
    }

    try {
      const cartDataContent = sections["cart-json"];

      if (this.allowDebug()) {
        console.log("CartStateManager: Parsing cart-json section", {
          contentLength: cartDataContent ? cartDataContent.length : 0,
          contentPreview: cartDataContent ? cartDataContent.substring(0, 200) : null
        });
      }

      // Parse the cart data from the script tag wrapper
      const parser = new DOMParser();
      const doc = parser.parseFromString(cartDataContent, "text/html");
      const scriptTag = doc.querySelector("#cart__json");

      if (!scriptTag) {
        console.error("CartStateManager: Script tag #cart__json not found in cart-json section");
        return null;
      }

      const enrichedCartData = JSON.parse(scriptTag.textContent);

      if (this.allowDebug()) {
        console.log("CartStateManager: Enriched cart data parsed from bundled response", {
          renderCartNote: enrichedCartData.renderCartNote,
          itemCount: enrichedCartData.itemCount,
          hasItems: enrichedCartData.items?.length || 0,
          hasCompareAtPriceTotals: !!enrichedCartData.compareAtPriceTotals
        });
      }

      return enrichedCartData;
    } catch (error) {
      console.error("CartStateManager: Error parsing enriched cart data from bundled response:", error);
      return null;
    }
  }

  /**
   * Fetch enriched cart data from cart-json section
   * @returns {Promise<Object>} Enriched cart data
   * @private
   */
  async #fetchEnrichedCartData() {
    if (this.allowDebug()) {
      console.log("CartStateManager: Fetching enriched cart data from cart-json section");
    }

    try {
      const response = await fetch(`${window.theme?.routes?.root_url}?sections=cart-json`);

      if (!response.ok) {
        throw new Error(
          `Failed to fetch cart-json section: ${response.status}`
        );
      }

      const data = await response.json();
      const enrichedCartData = this.#parseEnrichedCartData(data);

      if (!enrichedCartData) {
        throw new Error("Failed to parse enriched cart data");
      }

      return enrichedCartData;
    } catch (error) {
      console.error("CartStateManager: Error fetching enriched cart data:", error);
      throw error;
    }

  }

  /**
   * Update all [data-cart-count] elements in the DOM
   * @param {number|null} itemCount - Item count to display (defaults to current state)
   */
  updateAllCartCountElements(itemCount = null) {
    const count = itemCount ?? this.state.itemCount;
    const cartCountElements = document.querySelectorAll('[data-cart-count]');

    cartCountElements.forEach(element => {
      element.textContent = count;
      element.classList.toggle('hidden', count === 0);
    });

    if (this.allowDebug()) {
      console.log(`CartStateManager: Updated ${cartCountElements.length} cart count elements`, { count });
    }
  }
}

// Auto-initialize and set global reference when imported
let cartStateManagerInstance = null;

/**
 * Initialize the cart state manager
 * @returns {Promise<CartStateManager>} The initialized cart state manager instance
 */
export async function initializeCartStateManager() {
  if (cartStateManagerInstance) return cartStateManagerInstance;

  cartStateManagerInstance = new CartStateManager();
  await cartStateManagerInstance.initialize();

  return cartStateManagerInstance;
}

/**
 * Get the current cart state manager instance
 * @returns {CartStateManager|null} The cart state manager instance or null if not initialized
 */
export function getCartStateManager() {
  return cartStateManagerInstance;
}

// Auto-initialize for backward compatibility when not using ES6 modules
(() => {
  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', async () => {
      try {
        await initializeCartStateManager();
      } catch (error) {
        console.error('CartStateManager: Auto-initialization failed:', error);
      }
    });
  } else {
    // DOM is already ready
    initializeCartStateManager().then(() => {
    }).catch(error => {
      console.error('CartStateManager: Auto-initialization failed:', error);
    });
  }
})();

export default CartStateManager;
